import React, { useEffect, useState } from 'react';
import api from '../services/api';
import Modal from '../components/Modal';
import toast from 'react-hot-toast';
import { Plus, Trash2, Check } from 'lucide-react';
import { formatCurrency, formatDate, today, canModifyRecord, isAdminUser, approvalBadge } from '../utils/helpers';

const empty = { vehicle_id:'', maintenance_date:today(), maintenance_type:'service', description:'', odometer_reading:'', service_center:'', cost:0, next_service_date:'', next_service_km:'', invoice_no:'' };

export default function Maintenance() {
  const [list, setList] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const admin = isAdminUser();

  const load = () => api.get('/maintenance').then(r => setList(r.data.data || []));
  useEffect(() => {
    load();
    api.get('/vehicles').then(r => setVehicles(r.data.data || []));
  }, []);

  const save = async () => {
    if (!form.vehicle_id) return toast.error('Vehicle required');
    try { const r = await api.post('/maintenance', form); toast.success(r.data.message || 'Logged'); setOpen(false); setForm(empty); load(); }
    catch (e) { toast.error(e?.response?.data?.message || 'Error'); }
  };
  const del = async (id) => { if (!window.confirm('Delete?')) return; try { await api.delete('/maintenance/' + id); toast.success('Deleted'); load(); } catch (e) { toast.error('Error'); } };
  const approve = async (id) => { if (!window.confirm('Approve this maintenance entry?')) return; try { await api.post('/maintenance/' + id); toast.success('Approved'); load(); } catch (e) { toast.error(e?.response?.data?.message || 'Error'); } };

  const approved = list.filter(r => r.approval_status === 'approved');
  const total = approved.reduce((s, r) => s + parseFloat(r.cost || 0), 0);
  const pendingCount = list.filter(r => r.approval_status === 'pending').length;

  return (
    <div className="card">
      <div className="card-header">
        <h3>Vehicle Maintenance ({list.length}) — Total: {formatCurrency(total)}{pendingCount > 0 && <span className="badge badge-orange" style={{marginLeft:8,fontSize:11}}>{pendingCount} pending</span>}</h3>
        <button className="btn btn-blue" onClick={() => { setForm(empty); setOpen(true); }}><Plus size={14}/> Log Maintenance</button>
      </div>
      <div className="table-wrap">
        <table>
          <thead><tr><th>Date</th><th>Vehicle</th><th>Type</th><th>Description</th><th>Service Center</th><th className="td-num">Cost</th><th>Next Service</th><th>Approval</th><th>Actions</th></tr></thead>
          <tbody>
            {list.length ? list.map(m => {
              const badge = approvalBadge(m.approval_status);
              const canMod = canModifyRecord(m);
              const isPending = m.approval_status === 'pending';
              return (
                <tr key={m.id} style={isPending ? {background:'#fffbeb'} : {}}>
                  <td>{formatDate(m.maintenance_date)}</td>
                  <td><strong>{m.vehicle_no}</strong></td>
                  <td><span className="badge badge-blue">{m.maintenance_type}</span></td>
                  <td>{m.description || '-'}</td>
                  <td>{m.service_center || '-'}</td>
                  <td className="td-num">{formatCurrency(m.cost)}</td>
                  <td>{formatDate(m.next_service_date)}</td>
                  <td>
                    <span className={badge.cls}>{badge.label}</span>
                    {m.created_by_name && <small style={{display:'block',color:'#94a3b8',fontSize:10,marginTop:2}}>by {m.created_by_name}</small>}
                  </td>
                  <td>
                    {admin && isPending && <button className="btn btn-sm btn-green" onClick={()=>approve(m.id)} title="Approve"><Check size={12}/></button>}
                    {' '}
                    {canMod && <button className="btn btn-sm btn-red" onClick={()=>del(m.id)} title="Delete"><Trash2 size={12}/></button>}
                    {!canMod && !isPending && <span style={{color:'#94a3b8',fontSize:11}}>Locked</span>}
                  </td>
                </tr>
              );
            }) : <tr><td colSpan="9" className="empty">No maintenance records</td></tr>}
          </tbody>
        </table>
      </div>

      <Modal open={open} onClose={()=>setOpen(false)} title="Log Maintenance" size="lg"
        footer={<>
          <button className="btn btn-gray" onClick={()=>setOpen(false)}>Cancel</button>
          <button className="btn btn-blue" onClick={save}>Save</button>
        </>}>
        {!admin && (
          <div style={{background:'#fef3c7',border:'1px solid #fcd34d',padding:'10px 12px',borderRadius:8,marginBottom:14,fontSize:13,color:'#92400e'}}>
            Your entry will be saved as <strong>pending</strong> until an admin approves it.
          </div>
        )}
        <div className="form-grid">
          <div className="form-group"><label>Vehicle *</label>
            <select value={form.vehicle_id} onChange={e=>setForm({...form, vehicle_id:e.target.value})}>
              <option value="">-- Select Vehicle --</option>
              {vehicles.map(v=><option key={v.id} value={v.id}>{v.vehicle_no} — {v.driver_name||'No driver'}</option>)}
            </select>
          </div>
          <div className="form-group"><label>Date *</label><input type="date" value={form.maintenance_date} onChange={e=>setForm({...form, maintenance_date:e.target.value})}/></div>
          <div className="form-group"><label>Type</label>
            <select value={form.maintenance_type} onChange={e=>setForm({...form, maintenance_type:e.target.value})}>
              <option value="service">Service</option><option value="repair">Repair</option><option value="tyre">Tyre</option>
              <option value="battery">Battery</option><option value="oil_change">Oil Change</option>
              <option value="insurance">Insurance</option><option value="permit">Permit</option><option value="other">Other</option>
            </select>
          </div>
          <div className="form-group"><label>Odometer (KM)</label><input type="number" value={form.odometer_reading} onChange={e=>setForm({...form, odometer_reading:e.target.value})}/></div>
          <div className="form-group"><label>Service Center</label><input value={form.service_center} onChange={e=>setForm({...form, service_center:e.target.value})}/></div>
          <div className="form-group"><label>Invoice No</label><input value={form.invoice_no} onChange={e=>setForm({...form, invoice_no:e.target.value})}/></div>
          <div className="form-group"><label>Cost *</label><input type="number" value={form.cost} onChange={e=>setForm({...form, cost:e.target.value})}/></div>
          <div className="form-group"><label>Next Service Date</label><input type="date" value={form.next_service_date} onChange={e=>setForm({...form, next_service_date:e.target.value})}/></div>
          <div className="form-group"><label>Next Service KM</label><input type="number" value={form.next_service_km} onChange={e=>setForm({...form, next_service_km:e.target.value})}/></div>
          <div className="form-group full-width"><label>Description / Work Done</label><textarea value={form.description} onChange={e=>setForm({...form, description:e.target.value})}/></div>
        </div>
      </Modal>
    </div>
  );
}
